// src/components/ProjectInfoModal.jsx
import React from "react";
import { Modal, Row, Col, Badge } from "react-bootstrap";
import {
  FaUserCircle,
  FaUsers,
  FaMapMarkerAlt,
  FaBus,
  FaUserFriends,
  FaCalendarAlt,
  FaCertificate,
  FaRoute,
  FaClock,
  FaRegClock,
  FaTimes,
  FaCheck,
} from "react-icons/fa";
import "./ProjectInfoModal.css";

const ProjectInfoModal = ({ show, onHide, project }) => {
  if (!project) return null;

  const formatDateTime = (date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(date));
  };

  const TransportSection = () => (
    <div className="transport-section info-section">
      <div className="transport-header">
        <FaBus className="transport-icon" />
        <span className="transport-status">
          {project.location?.transportProvided ? (
            <>
              <FaCheck className="status-icon text-success" />
              Transport Provided
            </>
          ) : (
            <>
              <FaTimes className="status-icon text-danger" />
              No Transport Provided
            </>
          )}
        </span>
      </div>

      {project.location?.transportProvided && (
        <div className="transport-details">
          <div className="schedule-info">
            {/* Add pickup schedule if available */}
            <div className="pickup-info">
              <FaClock className="me-2" />
              <span>Pick-up Schedule:</span>
              <Badge bg="info" className="ms-2">
                As per project timing
              </Badge>
            </div>

            <div className="transport-type mt-2">
              <FaRoute className="me-2" />
              <span>Transport Type:</span>
              <Badge bg="secondary" className="ms-2">
                {project.location.transportType || "Standard"}
              </Badge>
            </div>
          </div>

          <div className="transport-notes mt-3">
            <h6 className="mb-2">Transport Details:</h6>
            <p className="mb-0">{project.location.transportDetails}</p>
          </div>

          {project.location.transportRules && (
            <div className="transport-rules mt-3">
              <h6 className="mb-2">Transport Rules:</h6>
              <ul className="rules-list">
                {project.location.transportRules.map((rule, index) => (
                  <li key={index}>{rule}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="text-primary">
          <div className="d-flex align-items-center">
            {project.organiserType === "Individual" ? (
              <FaUserCircle size={30} className="me-2" />
            ) : (
              <FaUsers size={30} className="me-2" />
            )}
            {project.name}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="py-4">
        {/* Organization and Time Frame Row */}
        <Row className="mb-4">
          <Col md={6}>
            <h5 className="mb-3 text-primary">
              <FaUserFriends className="me-2" />
              Organization Details
            </h5>
            <div className="info-section">
              <p>
                <strong>Organiser Type:</strong> {project.organiserType}
              </p>
              <p>
                <strong>Organiser Name:</strong> {project.organiserName}
              </p>
              <p>
                <strong>Project Visibility:</strong> {project.visibility}
              </p>
            </div>
          </Col>
          <Col md={6}>
            <h5 className="mb-3 text-primary">
              <FaCalendarAlt className="me-2" />
              Time Frame
            </h5>
            <div className="info-section">
              <p>
                <strong>Start Date:</strong> {formatDateTime(project.startDate)}
              </p>
              <p>
                <strong>End Date:</strong>{" "}
                {project.endDate ? formatDateTime(project.endDate) : "Ongoing"}
              </p>
              <p>
                <strong>Weekly Hours:</strong>{" "}
                {project.weeklyHoursRequired?.minimum} -{" "}
                {project.weeklyHoursRequired?.maximum} hours
              </p>
            </div>
          </Col>
        </Row>

        {/* Categories and SDGs Row */}
        <Row className="mb-4">
          <Col md={12}>
            <h5 className="mb-3 text-primary">
              <FaCertificate className="me-2" />
              Categories & SDGs
            </h5>
            <div className="info-section">
              <div className="mb-3">
                <strong>Categories:</strong>
                <br />
                {project.categories?.map((category, index) => (
                  <Badge bg="info" className="me-2 mb-2" key={index}>
                    {category}
                  </Badge>
                ))}
              </div>
              <div>
                <strong>SDGs:</strong>
                <br />
                {project.sdgs?.map((sdg, index) => (
                  <Badge bg="success" className="me-2 mb-2" key={index}>
                    {sdg}
                  </Badge>
                ))}
              </div>
            </div>
          </Col>
        </Row>

        {/* Location and Transport Section */}
        {project.locationType !== "Online" && (
          <>
            <Row className="mb-4">
              <Col md={12}>
                <h5 className="mb-3 text-primary">
                  <FaMapMarkerAlt className="me-2" />
                  Location Details
                </h5>
                <div className="info-section">
                  <p>
                    <strong>Location Type:</strong> {project.locationType}
                  </p>
                  <p>
                    <strong>Address:</strong> {project.location?.address}
                  </p>
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={12}>
                <h5 className="mb-3 text-primary">
                  <FaBus className="me-2" />
                  Transport Information
                </h5>
                <TransportSection />
              </Col>
            </Row>
          </>
        )}

        {/* Participant Requirements Row */}
        <Row>
          <Col md={12}>
            <h5 className="mb-3 text-primary">
              <FaUserFriends className="me-2" />
              Participant Requirements
            </h5>
            <div className="info-section">
              {project.participantTypes?.map((type, index) => (
                <div key={index} className="mb-3">
                  {type.role && (
                    <p>
                      <strong>Role:</strong> {type.role}
                    </p>
                  )}
                  <p>
                    <strong>Experience Level:</strong> {type.experienceLevel}
                  </p>
                  {type.skillsRequired?.length > 0 && (
                    <div className="mb-2">
                      <strong>Required Skills:</strong>
                      <br />
                      {type.skillsRequired.map((skill, idx) => (
                        <Badge bg="secondary" className="me-2 mb-2" key={idx}>
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p>
                    <strong>Age Range:</strong> {type.minimumAge} -{" "}
                    {type.maximumAge} years
                  </p>
                  {type.additionalRequirements && (
                    <p>
                      <strong>Additional Requirements:</strong>{" "}
                      {type.additionalRequirements}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default ProjectInfoModal;
